/**
 * FindByEntityDrawer — from one parcel to every parcel its registered owner
 * holds (AfriGIS deeds via API Manager), then link them to a legal entity.
 *
 * Each new AfriGIS lookup is billed, so the drawer only looks up on an explicit
 * click, shows the spend, and linking re-uses the cached result. When the
 * backend reports the lookup is not enabled or not configured, the drawer
 * explains why and makes no call.
 *
 * Opened from the header via the mapper:open-find-entity window event; an
 * event detail of { parcel_id } or { sg_code } pre-fills the anchor.
 */

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Lock, Loader2, Building2, Link2 } from 'lucide-react';
import { usePartyEntities } from '@/features/entity/api';
import {
  useMapperDeedsStatus,
  useMapperFindByEntity,
  type DeedsOwner,
  type DeedsPortfolioRequest,
  type DeedsPortfolioResult,
} from '../api';
import { MAPPER_OPEN_FIND_ENTITY_EVENT } from '../constants';

const SG_CODE_RE = /^[A-Za-z][A-Za-z0-9]{20}$/;
const LAT_LNG_RE = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;

const ERROR_TEXT: Record<string, string> = {
  deeds_disabled: 'Deeds lookup is not enabled on this platform.',
  deeds_connector_not_installed: 'AfriGIS Property Search is not installed. Install it in API Manager > Connectors and add its credentials.',
  deeds_credentials_missing: 'AfriGIS Property Search has no credentials yet. Add them in API Manager.',
  lookup_budget_exhausted: 'Daily AfriGIS lookup budget reached. Try again tomorrow or ask an operator to raise the limit.',
  no_parcel_at_point: 'None of our cadastral parcels contains that point. Enter the SG code instead.',
  anchor_sg_code_unknown: 'This parcel has no SG code on record. Enter the 21-character SG code instead.',
  invalid_sg_code: 'That is not a 21-character SG code (e.g. C00700070000011700000).',
  parcel_not_found: 'Parcel not found.',
  afrigis_auth_rejected: 'AfriGIS rejected the credentials. Check the key in API Manager.',
};

const PORTFOLIO_TEXT: Record<string, string> = {
  complete: 'All registered properties found.',
  vendor_endpoint_pending: 'AfriGIS has no trust or company portfolio lookup, so only properties already looked up are shown.',
  skipped_lookup_cap: 'Not expanded (lookup cap).',
  unsupported_owner: 'Owner type cannot be expanded.',
};

function parseAnchor(input: string, parcelId: string | null): DeedsPortfolioRequest | null {
  const value = input.trim();
  if (!value) return parcelId ? { parcel_id: parcelId } : null;
  if (SG_CODE_RE.test(value)) return { sg_code: value.toUpperCase() };
  const m = value.match(LAT_LNG_RE);
  if (m) return { lat: Number(m[1]), lng: Number(m[2]) };
  return null;
}

function ownerIdLabel(owner: DeedsOwner) {
  if (owner.registration_number) return owner.registration_number;
  if (owner.id_masked) return `ID ${owner.id_masked}`;
  return null;
}

function money(result: DeedsPortfolioResult) {
  const b = result.billing;
  if (!b) return null;
  const cost = b.estimated_cost != null && b.currency ? ` (${b.currency} ${b.estimated_cost})` : '';
  return `${b.billable_calls} billed lookup${b.billable_calls === 1 ? '' : 's'}${cost}, ${b.cached_calls} from cache`;
}

export default function FindByEntityDrawer() {
  const [open, setOpen] = useState(false);
  const [anchorInput, setAnchorInput] = useState('');
  const [anchorParcelId, setAnchorParcelId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<DeedsPortfolioRequest | null>(null);
  const [entityId, setEntityId] = useState('');
  const [result, setResult] = useState<DeedsPortfolioResult | null>(null);

  const { data: deeds } = useMapperDeedsStatus({ enabled: open });
  const { data: entityData } = usePartyEntities({ limit: 100, sort_by: 'name', sort_order: 'asc' });
  const entities = entityData?.entities ?? [];
  const findByEntity = useMapperFindByEntity();

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ parcel_id?: string; sg_code?: string } | undefined>).detail;
      setAnchorParcelId(detail?.parcel_id || null);
      setAnchorInput(detail?.sg_code || '');
      setResult(null);
      setOpen(true);
    };
    window.addEventListener(MAPPER_OPEN_FIND_ENTITY_EVENT, onOpen);
    return () => window.removeEventListener(MAPPER_OPEN_FIND_ENTITY_EVENT, onOpen);
  }, []);

  const ready = Boolean(deeds?.enabled && deeds?.configured);
  const parsedAnchor = parseAnchor(anchorInput, anchorParcelId);
  const linkable = useMemo(
    () => (result?.portfolio?.properties || []).filter((p) => p.parcel && p.parcel.linked_entity_id !== entityId),
    [result, entityId],
  );

  const run = async (body: DeedsPortfolioRequest) => {
    const res = await findByEntity.mutateAsync(body);
    setResult(res);
  };

  const handleLookup = async () => {
    if (!parsedAnchor) return;
    setAnchor(parsedAnchor);
    await run(parsedAnchor);
  };

  // Re-runs the same anchor (served from cache) with link: true.
  const handleLink = async () => {
    if (!anchor || !entityId) return;
    await run({ ...anchor, entity_id: entityId, link: true });
  };

  const errorText = result && !result.success
    ? ERROR_TEXT[result.error || ''] || result.message || 'Deeds lookup failed.'
    : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 bg-black/20"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ x: 380 }}
            animate={{ x: 0 }}
            exit={{ x: 380 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="absolute right-0 top-0 z-40 flex h-full w-[380px] flex-col border-l border-gray-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-emerald-600" />
                <span className="text-sm font-semibold text-gray-800">Find by legal entity</span>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {!ready ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Lock className="h-4 w-4" />
                    <span className="text-sm font-semibold">
                      {deeds?.enabled ? 'Deeds lookup not configured' : 'Deeds lookup not enabled'}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-amber-700">
                    {deeds?.message || 'The AfriGIS deeds lookup is not available. No external call is made.'}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-500">Start from a parcel</label>
                    <input
                      value={anchorInput}
                      onChange={(e) => setAnchorInput(e.target.value)}
                      placeholder={anchorParcelId ? 'Selected parcel (or type an SG code)' : 'SG code, or lat, lng'}
                      className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-2 font-mono text-xs text-gray-700 outline-none focus:border-emerald-300"
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      e.g. C00700070000011700000 or -27.9868, 24.7290. A point is matched on our cadastre first.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleLookup}
                    disabled={!parsedAnchor || findByEntity.isPending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {findByEntity.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />}
                    Look up registered owner
                  </button>
                  <p className="text-[11px] leading-relaxed text-gray-400">
                    One billed AfriGIS lookup per new parcel (repeat lookups are cached), plus one per natural-person owner.
                  </p>

                  {errorText && (
                    <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">{errorText}</div>
                  )}

                  {result?.success && result.status === 'deeds_not_found' && (
                    <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                      No Deeds Office record for SG {result.anchor?.sg_code}.
                    </div>
                  )}

                  {result?.success && result.status === 'ok' && (
                    <div className="space-y-3">
                      {(result.owners || []).map((owner) => (
                        <div key={owner.entity_key || owner.name || 'owner'} className="rounded-lg border border-gray-100 p-3">
                          <div className="text-xs font-semibold text-gray-800">{owner.name || 'Unknown owner'}</div>
                          <div className="mt-0.5 text-[11px] capitalize text-gray-500">
                            {owner.type}
                            {ownerIdLabel(owner) ? ` · ${ownerIdLabel(owner)}` : ''}
                          </div>
                          {owner.portfolio_status && (
                            <div className="mt-1.5 text-[11px] leading-relaxed text-gray-500">{PORTFOLIO_TEXT[owner.portfolio_status] || owner.portfolio_status}</div>
                          )}
                        </div>
                      ))}

                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-500">
                          Properties ({result.portfolio?.matched ?? 0} in our cadastre, {result.portfolio?.unmatched ?? 0} not)
                        </div>
                        <ul className="space-y-1">
                          {(result.portfolio?.properties || []).map((p) => (
                            <li key={p.lpicode} className="rounded bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600">
                              <div className="font-mono">{p.lpicode}</div>
                              <div>
                                {[p.property_type, p.title_deed, p.extent_ha != null ? `${p.extent_ha} ha` : null].filter(Boolean).join(' · ')}
                              </div>
                              <div className={p.parcel ? 'text-emerald-700' : 'text-amber-700'}>
                                {p.parcel
                                  ? `Parcel ${p.parcel.farm_name || p.parcel.id}${p.parcel.linked_entity_id ? ' (linked)' : ''}`
                                  : 'Not in our cadastre'}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {money(result) && <div className="text-[11px] text-gray-400">{money(result)}</div>}

                      <div className="border-t border-gray-100 pt-3">
                        <label className="mb-1.5 block text-xs font-medium text-gray-500">Link matched parcels to</label>
                        <select
                          value={entityId}
                          onChange={(e) => setEntityId(e.target.value)}
                          className="w-full rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs text-gray-700 outline-none focus:border-emerald-300"
                        >
                          <option value="">Select a legal entity…</option>
                          {entities.map((entity) => (
                            <option key={entity.id} value={entity.id}>{entity.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleLink}
                          disabled={!entityId || !linkable.length || findByEntity.isPending}
                          className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-3 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:opacity-40"
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          Link {linkable.length} parcel{linkable.length === 1 ? '' : 's'}
                        </button>
                        {result.link && (
                          <p className="mt-2 text-[11px] text-gray-500">
                            Linked {result.link.linked.filter((l) => !l.error).length}
                            {result.link.skipped.length ? `, skipped ${result.link.skipped.length} (already linked)` : ''}.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
